import { defineStore } from 'pinia';
import OnixActionsAPI from 'dashboard/api/onix/actions';
import { throwErrorMessage } from 'dashboard/store/utils/api';

export const useOnixActionsStore = defineStore('onixActions', {
  state: () => ({
    records: [],
    uiFlags: {
      fetchingList: false,
      creatingItem: false,
      updatingItem: false,
      deletingItem: false,
      testingItem: false,
    },
  }),

  getters: {
    getActions: state => state.records,
    getVerifiedActions: state =>
      state.records.filter(a => a.verified_at && a.enabled !== false),
    getUIFlags: state => state.uiFlags,
  },

  actions: {
    setUIFlag(data) {
      this.uiFlags = { ...this.uiFlags, ...data };
    },

    replaceRecord(record) {
      if (!record?.id) return;
      const exists = this.records.some(item => item.id === record.id);
      this.records = exists
        ? this.records.map(item => (item.id === record.id ? record : item))
        : [record, ...this.records];
    },

    async fetch(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await OnixActionsAPI.get(params);
        this.records = data.payload || [];
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async fetchOne(id) {
      try {
        const { data } = await OnixActionsAPI.show(id);
        this.replaceRecord(data);
        return data;
      } catch {
        return null;
      }
    },

    async create(payload) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await OnixActionsAPI.create(payload);
        this.replaceRecord(data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return null;
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update(id, payload) {
      this.setUIFlag({ updatingItem: true });
      try {
        const { data } = await OnixActionsAPI.update(id, payload);
        this.replaceRecord(data);
        return data;
      } catch (error) {
        throwErrorMessage(error);
        return null;
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async remove(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await OnixActionsAPI.delete(id);
        this.records = this.records.filter(item => item.id !== Number(id));
        return true;
      } catch {
        return false;
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async test(id, params = {}) {
      this.setUIFlag({ testingItem: true });
      try {
        const { data } = await OnixActionsAPI.test(id, params);
        this.replaceRecord(data.action);
        return data;
      } catch (error) {
        const body = error?.response?.data;
        if (body?.action) this.replaceRecord(body.action);
        if (body?.result) return body;
        throwErrorMessage(error);
        return null;
      } finally {
        this.setUIFlag({ testingItem: false });
      }
    },
  },
});
